import { collegamentoNotifica } from './notifiche.ts'
import { etichettaProvenienza, type EtichettaProvenienza } from './regole-provenienza.ts'
import { ePaginaPosta } from './posta-finestra.ts'

/**
 * Avvisi sullo schermo (richiesta di Davide del 2/11/2026): che cosa mostrare e che cosa dire.
 *
 * Il riquadro in basso a destra, con il gestionale visibile, dice mittente e oggetto di una email e il
 * titolo di una notifica. La notifica del sistema operativo, che resta nel centro notifiche del computer
 * e la legge chiunque passi davanti allo schermo, dice solo «Nuova email in <casella>» o «Nuovo avviso nel
 * gestionale», come la push del telefono (apps/api/src/servizi/push.ts).
 *
 * Senza React e senza il browser: le regole sono provate in apps/api/test/avvisi-regole.test.ts.
 */

/** La risposta di GET /api/avvisi/novita (apps/api/src/servizi/novita.ts). */
export type Novita = {
  adesso: string
  da_leggere: number
  email: {
    id: string; thread_id: string | null; casella_id: string; casella: string
    mittente: { nome: string | null; indirizzo: string }; oggetto: string; provenienza: string | null
    arrivata_il: string
  }[]
  email_altre: number
  notifiche: {
    id: string; tipo: string; titolo: string; oggetto_tipo: string | null; oggetto_id: string | null
    persona_id: string | null; creata_il: string
  }[]
  notifiche_altre: number
}

export type Voce = {
  /** 'email:<id>', 'notifica:<id>', 'riepilogo:<n>': identifica la voce e fa da `tag` della notifica del sistema. */
  chiave: string
  genere: 'email' | 'notifica' | 'riepilogo'
  /** Il tipo della notifica (ref.tipo_notifica); null per email e riepiloghi. */
  tipo: string | null
  /** Riga piccola in cima al riquadro. */
  intestazione: string
  titolo: string
  testo: string | null
  /** Dove porta il clic, nel gestionale. */
  dove: string
  /** Il solo testo della notifica del sistema: niente mittente, oggetto o titolo. */
  sistema: string
  threadId: string | null
  notificaId: string | null
  /** Email che si presenta come CIDIS senza esserlo, o esterna non confermata dal suo dominio (522). */
  allarme: EtichettaProvenienza | null
  /** Istante d'arrivo, in millisecondi. */
  il: number
}

/** Riquadri al massimo sullo schermo, impilati. */
export const RIQUADRI_MASSIMI = 3

/** Notifiche del sistema al massimo per giro: le altre in una sola, «Altre N novità». */
export const NOTIFICHE_SISTEMA_MASSIME = 3

/** Il collegamento a una conversazione, nel formato che la pagina Posta apre: casella e thread. */
export function collegamentoEmail(e: { casella_id: string; thread_id: string | null }): string {
  const q = new URLSearchParams({ casella: e.casella_id })
  if (e.thread_id) q.set('thread', e.thread_id)
  return `/posta?${q.toString()}`
}

/** La conversazione aperta a schermo, dall'indirizzo della pagina: il suo riquadro non serve. Anche nella finestra della posta. */
export function threadAperto(percorso: string, ricerca: string): string | null {
  if (!ePaginaPosta(percorso)) return null
  return new URLSearchParams(ricerca).get('thread')
}

export function sistemaEmail(casella: string): string {
  return `Nuova email in ${casella}`
}
export const SISTEMA_NOTIFICA = 'Nuovo avviso nel gestionale'

/**
 * Messaggio diretto nel messenger (530): l'avviso e' gia' generico, e la notifica del sistema dice lo
 * stesso. Chi e' nella pagina «Messenger» non riceve il riquadro: il messaggio ce l'ha davanti.
 */
export const TIPO_MESSENGER = 'messenger.messaggio'
export const SISTEMA_MESSENGER = 'Nuovo messaggio nel messenger'

/** Le voci da mostrare in questa pagina: senza la conversazione aperta e, nel messenger, senza i suoi avvisi. */
export function daMostrareQui(voci: Voce[], percorso: string, threadAperto: string | null): Voce[] {
  return voci.filter((v) => !(threadAperto && v.threadId === threadAperto)
    && !(percorso === '/messaggi' && v.tipo === TIPO_MESSENGER))
}

const ALLARMI = new Set(['falsa_cidis', 'esterna_non_verificata'])

/**
 * Le voci nuove di un giro, dalla piu' recente: le email prima, poi le notifiche. Restano fuori quelle
 * gia' viste (da questa o da un'altra scheda) e quelle arrivate prima della `partenza`, l'istante del
 * primo giro: il server rilegge qualche minuto indietro, per non perdere gli arrivi a cavallo di un giro.
 */
export function vociNuove(n: Novita, visti: ReadonlySet<string>, partenza: number): Voce[] {
  const email = n.email.map((e): Voce => {
    const scritto = e.mittente.nome ? `${e.mittente.nome} <${e.mittente.indirizzo}>` : e.mittente.indirizzo
    return {
      chiave: `email:${e.id}`, genere: 'email', tipo: null,
      intestazione: `Nuova email · ${e.casella}`,
      titolo: e.mittente.nome || e.mittente.indirizzo || 'Mittente sconosciuto',
      testo: e.oggetto || '(senza oggetto)',
      dove: collegamentoEmail(e),
      sistema: sistemaEmail(e.casella),
      threadId: e.thread_id, notificaId: null,
      allarme: e.provenienza && ALLARMI.has(e.provenienza)
        ? etichettaProvenienza({ direzione: 'entrata', da: scritto, provenienza: e.provenienza }, null)
        : null,
      il: Date.parse(e.arrivata_il),
    }
  })
  const notifiche = n.notifiche.map((x): Voce => ({
    chiave: `notifica:${x.id}`, genere: 'notifica', tipo: x.tipo,
    intestazione: x.tipo === TIPO_MESSENGER ? 'Messenger' : 'Notifica del gestionale',
    titolo: x.titolo,
    testo: null,
    dove: collegamentoNotifica(x)?.a ?? '/notifiche',
    sistema: x.tipo === TIPO_MESSENGER ? SISTEMA_MESSENGER : SISTEMA_NOTIFICA,
    threadId: null, notificaId: x.id, allarme: null,
    il: Date.parse(x.creata_il),
  }))
  return [...email, ...notifiche].filter((v) => !visti.has(v.chiave) && !(v.il <= partenza))
}

/** Una voce che riassume quelle che non trovano posto: porta alla Posta se sono tutte email. */
export function riepilogo(quante: number, soloEmail: boolean): Voce {
  const testo = quante === 1 ? 'Un’altra novità' : `Altre ${quante} novità`
  return {
    chiave: `riepilogo:${quante}:${soloEmail ? 'posta' : 'tutte'}`, genere: 'riepilogo', tipo: null,
    intestazione: 'Gestionale CIDIS',
    titolo: testo,
    testo: soloEmail ? 'Apri «Posta» per vederle.' : 'Apri «Notifiche» e «Posta» per vederle.',
    dove: soloEmail ? '/posta' : '/notifiche',
    sistema: `${testo} nel gestionale`,
    threadId: null, notificaId: null, allarme: null, il: 0,
  }
}

/**
 * I riquadri dopo l'arrivo di voci nuove: le nuove in fondo, piu' vicine all'angolo; al massimo
 * RIQUADRI_MASSIMI. Se non c'e' posto per tutte, l'ultimo posto in alto diventa «Altre N novità», che
 * conta anche le voci che il server non ha mandato (`altre`) e un eventuale riepilogo precedente.
 */
export function accoda(attuali: Voce[], nuove: Voce[], altre = 0, soloEmail = false): Voce[] {
  let contate = altre
  let tutteEmail = soloEmail || altre === 0
  const voci: Voce[] = []
  // Le nuove arrivano dalla piu' recente: in fondo va la piu' recente.
  for (const v of [...attuali, ...[...nuove].sort((a, b) => a.il - b.il)]) {
    if (v.genere === 'riepilogo') {
      contate += Number(v.chiave.split(':')[1]) || 0
      tutteEmail &&= v.dove === '/posta'
    } else if (!voci.some((x) => x.chiave === v.chiave)) voci.push(v)
  }
  if (!contate && voci.length <= RIQUADRI_MASSIMI) return voci
  const posto = RIQUADRI_MASSIMI - 1
  const fuori = voci.slice(0, Math.max(0, voci.length - posto))
  const quante = contate + fuori.length
  return [riepilogo(quante, tutteEmail && fuori.every((v) => v.genere === 'email')), ...voci.slice(-posto)]
}

/** Le notifiche del sistema di un giro: le prime, piu' una sola per le altre. */
export function perIlSistema(voci: Voce[], altre: number, soloEmail: boolean): Voce[] {
  if (voci.length <= NOTIFICHE_SISTEMA_MASSIME && !altre) return voci
  const posto = NOTIFICHE_SISTEMA_MASSIME - 1
  const fuori = voci.slice(posto)
  return [...voci.slice(0, posto), riepilogo(fuori.length + altre, soloEmail && fuori.every((v) => v.genere === 'email'))]
}
